import { create } from "zustand";

export type TNotificationType =
  | "chapter_ready"
  | "chat_responded"
  | "outline_ready"
  | "slides_ready"
  | "outline_outdated";

export type TNotification = {
  id: number;
  type: TNotificationType;
  isRead: boolean;
  projectChapterId: number | null;
  projectId: number | null;
  userId: number;
  createdAt: string;
  updatedAt: string;
  projectChapter: {
    id: number;
    projectTemplateChapterSlug: string | null;
    projectId: number;
    project: {
      id: number;
      name: string;
    };
    projectTemplateChapter: {
      name: string;
      slug: string;
    } | null;
  } | null;
  project: {
    id: number;
    name: string;
  } | null;
};

type TNotificationStore = {
  notifications: TNotification[];

  setNotifications: (notifications: TNotification[]) => void;
  addNotification: (notification: TNotification) => void;
  removeProjectNotifications: (projectId: number) => void;
  markChapterAsRead: (chapterId: number) => boolean;
  hasChapterNotification: (chapterId: number) => boolean;
};

export const useNotificationStore = create<TNotificationStore>((set, get) => ({
  notifications: [],

  setNotifications: (notifications: TNotification[]) => {
    set({ notifications });
  },

  addNotification: (notification: TNotification) => {
    if (get().notifications.some((n) => n.id === notification.id)) {
      return;
    }
    set({ notifications: [notification, ...get().notifications] });
  },

  removeProjectNotifications: (projectId: number) => {
    set({
      notifications: get().notifications.filter((n) => {
        const nProjectId = n.projectChapter?.projectId ?? n.projectId ?? n.project?.id;
        return nProjectId !== projectId;
      })
    });
  },

  markChapterAsRead: (chapterId: number) => {
    let changedChaptersCounter = 0;
    set({
      notifications: get().notifications.map((n) => {
        if (n.projectChapterId != null && n.projectChapterId === chapterId) {
          if (!n.isRead) changedChaptersCounter++;
          return { ...n, isRead: true };
        }
        return n;
      })
    });
    return changedChaptersCounter > 0;
  },

  hasChapterNotification: (chapterId: number) => {
    return Boolean(get().notifications.find((n) => n.projectChapterId != null && n.projectChapterId === chapterId && !n.isRead));
  }
}));
